import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/layout/PageLayout';
import ShareButtons from '@/components/ShareButtons';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Twitter, Linkedin } from 'lucide-react';
import api from '@/lib/api';


const NewsDetail = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await api.get(`/news/${id}`);
        setArticle(response.data);
      } catch (error) {
        console.error('Failed to fetch article', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = article?.title || 'News Article';
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  if (loading) {
    return (
      <PageLayout mainClassName="flex items-center justify-center">
        <p className="font-sans text-lg text-muted-foreground">Loading...</p>
      </PageLayout>
    );
  }

  if (!article) {
    return (
      <PageLayout mainClassName="flex flex-col items-center justify-center px-6">
        <h1 className="font-serif text-4xl font-bold text-primary mb-4">Article Not Found</h1>
        <p className="font-sans text-lg text-muted-foreground mb-8">
          The article you're looking for doesn't exist or has been removed.
        </p>
        <Button asChild>
          <Link to="/">
            <ArrowLeft size={20} /> Back to Home
          </Link>
        </Button>
      </PageLayout>
    );
  }

  return (
    <PageLayout mainClassName="py-12 md:py-20 px-6 md:px-12 bg-background">
      <article className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link
          to="/"
          className="inline-flex items-center font-sans text-sm text-accent hover:text-accent/80 mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft size={16} className="mr-2" /> Back to Home
        </Link>

        {/* Article Header */}
        <header className="mb-10">
          <p className="font-sans text-sm tracking-wide uppercase text-accent mb-3" data-testid="article-category">
            {article.category}
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6 leading-tight" data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex items-center justify-between flex-wrap gap-4 pb-8 border-b border-border">
            <div className="font-sans text-sm text-muted-foreground">
              <span data-testid="article-author">By {article.author}</span>
              <span className="mx-2" aria-hidden="true">•</span>
              <span data-testid="article-date">
                {new Date(article.published_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>

            {/* Social Share Buttons */}
            <ShareButtons url={shareUrl} title={shareTitle} />
          </div>
        </header>

        {/* Article Content */}
        <div
          className="prose prose-lg max-w-none font-sans text-foreground leading-relaxed"
          data-testid="article-content"
        >
          {article.content.split('\n').map((paragraph, index) => (
            <p key={index} className="mb-6 text-base md:text-lg leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        {/* Bottom Share Section */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-sans text-sm text-muted-foreground">
              Found this article helpful? Share it with others.
            </p>
            <div className="flex items-center gap-3">
              <a
                href={shareLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors font-sans text-sm"
              >
                <Twitter size={16} className="mr-2" /> Tweet
              </a>
              <a
                href={shareLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-accent text-accent-foreground rounded-full hover:bg-accent/90 transition-colors font-sans text-sm"
              >
                <Linkedin size={16} className="mr-2" /> Share
              </a>
            </div>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="mt-12 text-center">
          <Button asChild variant="outline" size="lg" data-testid="back-to-home-bottom">
            <Link to="/">
              <ArrowLeft size={20} /> Back to All News
            </Link>
          </Button>
        </div>
      </article>
    </PageLayout>
  );
};

export default NewsDetail;
